<template>
  <div class="arcgis-map-container">
    <!-- Map Controls -->
    <div class="map-controls mb-3">
      <b-row>
        <!-- Basemap Selector -->
        <b-col md="6">
          <b-form-group
            label="نوع الخريطة الأساسية"
            label-for="basemap-select"
          >
            <v-select
              id="basemap-select"
              v-model="selectedBasemap"
              :options="basemaps"
              :reduce="val => val.value"
              :get-option-label="option => option.label"
              @input="changeBasemap"
            >
              <template #option="{ label }">
                <span>{{ label }}</span>
              </template>
              <template #selected-option="{ label }">
                <span>{{ label }}</span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Search Input -->
        <b-col md="6">
          <b-form-group
            label="البحث عن موقع"
            label-for="search-input"
          >
            <b-input-group>
              <b-form-input
                id="search-input"
                v-model="searchQuery"
                placeholder="ابحث عن مكان..."
                @keyup.enter="searchLocation"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  @click="searchLocation"
                >
                  <feather-icon
                    icon="SearchIcon"
                    class="mr-50"
                  />
                  بحث
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Map Container -->
    <div class="map-container-wrapper">
      <div
        v-if="isLoading"
        class="loading-overlay"
      >
        <div class="spinner" />
      </div>
      <div
        id="viewDiv"
        class="map-view"
      />
    </div>

    <!-- Map Info -->
    <div class="map-info mt-2">
      <b-button
        v-if="polygon"
        variant="success"
        class="mb-2"
        @click="confirmPolygon"
      >
        اعتماد المضلع
      </b-button>
      <b-row>
        <b-col md="6">
          <small class="text-muted">
            إحداثيات الموقع: {{ currentCoordinates.lat.toFixed(6) }}, {{ currentCoordinates.lng.toFixed(6) }}
          </small>
        </b-col>
        <b-col
          md="6"
          class="text-right"
        >
          <small class="text-muted">
            مستوى التكبير: {{ currentZoom }}
          </small>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
} from 'bootstrap-vue'
import { loadArcgisModules } from '@/utils/arcgisLoader'
import { mapGetters } from 'vuex'

export default {
  name: 'ArcGisMap',
  components: {
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
  },
  props: {
    initialPolygon: {
      type: [Object, Array],
      default: null,
    },
  },
  data() {
    return {
      polygon: null,
      polygonCoords: null,
      polygonArea: null,
      view: null,
      map: null,
      selectedBasemap: 'hybrid',
      searchQuery: '',
      currentCoordinates: {
        lat: 31.2001, // Default to Cairo, Egypt
        lng: 29.9187,
      },
      currentZoom: 18,
      basemaps: [
        { value: 'satellite', label: 'الخرائط الفضائية' },
        { value: 'streets', label: 'الخرائط العادية' },
        { value: 'hybrid', label: 'مختلط' },
        // { value: 'streets-navigation-vector', label: 'الخرائط العادية مع مسارات المشاة والسيارات' },
        { value: 'topo', label: 'طبوغرافي' },
        { value: 'gray', label: 'رمادي' },
        { value: 'dark-gray', label: 'رمادي داكن' },
        { value: 'oceans', label: 'المحيطات' },
      ],
      widgets: {
        search: null,
        home: null,
      },
      arcgisModules: null,
      isLoading: false,
      sketch: null,
      fixedPointsLayer: null,
      locationPolygonsLayer: null,
    }
  },
  computed: {
    ...mapGetters({
      fixedPoints: 'fixedPoints/fixedPoints',
      fixedPointsLoading: 'fixedPoints/load',
      storeLocationPolygons: 'projects/locationPolygons',
      loadStoreLocationPolygons: 'projects/loadLocationPolygons',
    }),
  },
  watch: {
    fixedPoints: {
      handler() {
        this.renderFixedPoints()
      },
      deep: true,
    },
  },
  async mounted() {
    // Try to get geolocation before map creation
    await this.setCurrentToGeolocation()
    try {
      const modules = await loadArcgisModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/widgets/Search',
        'esri/widgets/Home',
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
        'esri/geometry/Point',
        'esri/symbols/SimpleMarkerSymbol',
        'esri/symbols/PictureMarkerSymbol',
        'esri/widgets/Sketch',
        'esri/geometry/geometryEngine',
        'esri/geometry/support/webMercatorUtils',
        'esri/geometry/Extent',
      ])

      this.arcgisModules = {
        Map: modules[0],
        MapView: modules[1],
        Search: modules[2],
        Home: modules[3],
        GraphicsLayer: modules[4],
        Graphic: modules[5],
        Point: modules[6],
        SimpleMarkerSymbol: modules[7],
        PictureMarkerSymbol: modules[8],
        Sketch: modules[9],
        geometryEngine: modules[10],
        webMercatorUtils: modules[11],
        Extent: modules[12],
      }
      this.createMap()
    } catch (error) {
      console.error('Failed to load ArcGIS modules:', error)
    }
  },
  beforeDestroy() {
    if (this.view) {
      this.view.destroy()
    }
  },
  methods: {
    confirmPolygon() {
      this.$emit('polygon-created', this.polygon.rings, this.polygonCoords, this.polygonArea)
      // this.polygon = null;
      // this.polygonCoords = null;
      // this.polygonArea = null;
    },
    createMap() {
      const { Map, MapView, GraphicsLayer } = this.arcgisModules
      // Create the map
      this.map = new Map({
        basemap: this.selectedBasemap,
      })

      // Create the view
      this.view = new MapView({
        container: 'viewDiv',
        map: this.map,
        center: [this.currentCoordinates.lng, this.currentCoordinates.lat],
        zoom: this.currentZoom,
      })

      // Create graphics layer for markers
      const graphicsLayer = new GraphicsLayer()
      this.map.add(graphicsLayer)
      this.graphicsLayer = graphicsLayer

      // Create separate graphics layer for fixed points
      this.fixedPointsLayer = new GraphicsLayer({
        title: 'Fixed Points',
        visible: true,
      })
      this.map.add(this.fixedPointsLayer)

      // Create layer for project/location polygons
      this.locationPolygonsLayer = new GraphicsLayer({
        title: 'Project Locations',
        visible: true,
      })
      this.map.add(this.locationPolygonsLayer)

      // Wait for the view to be ready before adding widgets
      this.view.when(() => {
        console.log('View is ready. Adding widgets.')

        // Create search widget
        this.createSearchWidget()

        // Create home widget
        this.createHomeWidget()

        // Initialize Sketch widget and add its UI to the map
        this.initializeSketch()

        // Load and render fixed points
        this.fetchAndRenderFixedPoints()

        // Load and render project/location polygons around current user location (500m radius)
        this.loadAndRenderLocationPolygons()

        console.log('Widgets added. Current UI components:', this.view.ui.components.items.map(item => item.declaredClass))
      })

      // Watch for view changes
      this.view.watch('center', center => {
        if (center) {
          this.currentCoordinates.lat = center.latitude
          this.currentCoordinates.lng = center.longitude
        }
      })

      this.view.watch('zoom', zoom => {
        this.currentZoom = Math.round(zoom)
      })

      // Listen for view updates to finish and hide loading
      this.view.watch('updating', updating => {
        if (!updating) {
          this.isLoading = false
        }
      })

      // Add click event to place markers
      this.view.on('click', event => {
        this.addMarkerAtPoint(event.mapPoint)
      })

      // Emit map ready event
      this.$emit('map-ready', this.view)
    },

    createSearchWidget() {
      const { Search } = this.arcgisModules
      const searchWidget = new Search({
        view: this.view,
        allPlaceholder: 'البحث عن مكان...',
        includeDefaultSources: true,
        container: document.createElement('div'),
      })

      // Store the search widget for later use
      this.searchWidget = searchWidget

      // Listen to search results
      searchWidget.on('search-complete', event => {
        if (event.results && event.results.length > 0) {
          const result = event.results[0]
          if (result.extent) {
            this.view.goTo(result.extent).then(() => {
              // Add marker at the searched location if it has a point
              if (result.feature && result.feature.geometry) {
                this.addMarkerAtPoint(result.feature.geometry, result.name)
              }
            })
          }
        }
      })
    },

    createHomeWidget() {
      const { Home } = this.arcgisModules
      const homeWidget = new Home({
        view: this.view,
      })

      // Add click event: on return home, update to geolocation
      homeWidget.on('go', async () => {
        await this.setCurrentToGeolocation()
        if (this.view && this.currentCoordinates.lat && this.currentCoordinates.lng) {
          this.view.goTo({ center: [this.currentCoordinates.lng, this.currentCoordinates.lat], zoom: 18 })
        }
      })

      this.view.ui.add(homeWidget, 'top-left')
    },

    initializeSketch() {
      const { Sketch, geometryEngine } = this.arcgisModules
      const sketch = new Sketch({
        layer: this.graphicsLayer,
        view: this.view,
        tooltipOptions: {
          enabled: true,
        },
        snappingOptions: {
          enabled: true,
        },

      })

      console.log('Sketch widget created:', sketch)
      this.view.ui.add(sketch, 'top-right')
      this.sketch = sketch

      sketch.viewModel.on('create', event => {
        if (event.state === 'complete') {
          if (event.graphic.geometry.type === 'polygon') {
            const polygon = event.graphic.geometry
            const coords = this.convertToDMS(polygon.rings)
            const areaSqM = geometryEngine.geodesicArea(polygon, 'square-meters')
            // console.log('areaSqM',areaSqM);
            // console.log('coords',coords);
            // console.log('polygon',polygon.rings);
            // fire the polygon-created with the ok button clicked
            this.polygon = polygon
            this.polygonCoords = coords
            this.polygonArea = areaSqM
            // this.polygon = polygon;
            // this.polygonCoords = coords;
            // this.polygonArea = areaSqM;
            // this.firePolygonCreated();
          } else if (event.graphic.geometry.type === 'polyline') {
            const polyline = event.graphic.geometry
            const length = geometryEngine.geodesicLength(polyline, 'meters')
            this.graphicsLayer.remove(event.graphic) // Remove after measuring
          }
        }
      })
    },

    firePolygonCreated() {
      this.$emit('polygon-created', this.polygon, this.polygonCoords, this.polygonArea)
    },
    convertToDMS(rings) {
      const { webMercatorUtils } = this.arcgisModules || {}
      if (!webMercatorUtils || typeof webMercatorUtils.xyToLngLat !== 'function') {
        console.warn('webMercatorUtils not available; returning original coordinates.')
        return rings
      }

      return rings.map(ring => ring.map(coord => {
        if (Array.isArray(coord) && coord.length >= 2) {
          const [longitude, latitude] = webMercatorUtils.xyToLngLat(coord[0], coord[1])
          return [longitude, latitude]
          // return [
          //   this.decimalToDMS(latitude, 'lat'),
          //   this.decimalToDMS(longitude, 'lng')
          // ];
        }
        return coord
      }))
    },

    decimalToDMS(value, type) {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return value
      }

      const absolute = Math.abs(value)
      const degrees = Math.floor(absolute)
      const minutesNotTruncated = (absolute - degrees) * 60
      const minutes = Math.floor(minutesNotTruncated)
      const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2)

      let direction
      if (type === 'lat') {
        direction = value >= 0 ? 'N' : 'S'
      } else if (type === 'lng') {
        direction = value >= 0 ? 'E' : 'W'
      } else {
        direction = ''
      }

      return `${degrees}°${minutes}'${seconds}"${direction}`
    },

    changeBasemap(newBasemap) {
      if (this.map && this.map.basemap.id !== newBasemap) {
        this.isLoading = true
        this.map.basemap = newBasemap
      }
    },

    async searchLocation() {
      if (!this.searchQuery.trim()) {
        return
      }

      try {
        // Use the Search widget to perform the search
        if (this.searchWidget) {
          const results = await this.searchWidget.search(this.searchQuery)

          if (results && results.length > 0) {
            const result = results[0]

            // Go to the result
            if (result.extent) {
              await this.view.goTo(result.extent)
            } else if (result.feature && result.feature.geometry) {
              await this.view.goTo({
                target: result.feature.geometry,
                zoom: 15,
              })
            }

            // Add marker if we have a geometry
            if (result.feature && result.feature.geometry) {
              this.addMarkerAtPoint(result.feature.geometry, result.name)
            }
          } else {

          }
        } else {

        }
      } catch (error) {
        console.error('Search error:', error)
      }
    },

    addMarkerAtPoint(point, title = 'موقع') {
      if (!this.graphicsLayer || !this.arcgisModules) return

      const { SimpleMarkerSymbol, Graphic } = this.arcgisModules

      const symbol = new SimpleMarkerSymbol({
        color: [226, 119, 40],
        outline: {
          color: [255, 255, 255],
          width: 2,
        },
        size: '12px',
      })

      const graphic = new Graphic({
        geometry: point,
        symbol,
        attributes: {
          title,
        },
      })

      // Clear previous markers if desired
      // this.graphicsLayer.removeAll()

      this.graphicsLayer.add(graphic)

      // Emit marker added event
      this.$emit('marker-added', {
        point,
        title,
      })
    },

    async loadAndRenderLocationPolygons() {
      try {
        console.log('loadAndRenderLocationPolygons: Starting...')

        // Ensure we have the latest user location
        await this.setCurrentToGeolocation()
        console.log('loadAndRenderLocationPolygons: Current location:', this.currentCoordinates)

        // Wait for layer to be ready
        if (!this.locationPolygonsLayer) {
          console.warn('loadAndRenderLocationPolygons: locationPolygonsLayer not ready yet')
          return
        }

        const query = {
          currentLocation: {
            latitude: this.currentCoordinates.lat,
            longitude: this.currentCoordinates.lng,
          },
        }

        console.log('loadAndRenderLocationPolygons: Dispatching with query:', query)
        const response = await this.$store.dispatch('projects/locationPolygons', { query })
        console.log('loadAndRenderLocationPolygons: Store response:', response)

        // Check store getter
        const polygons = this.storeLocationPolygons || []
        console.log('loadAndRenderLocationPolygons: Polygons from store:', polygons)
        console.log('loadAndRenderLocationPolygons: Polygon count:', polygons.length)

        if (!polygons.length) {
          console.warn('loadAndRenderLocationPolygons: No polygons found in response')
          if (this.$toast) {
            this.$toast.info('لا توجد مشاريع ضمن نطاق 2000 متر من موقعك الحالي.')
          }
          return
        }

        console.log('loadAndRenderLocationPolygons: Drawing polygons...')
        this.drawLocationPolygons(polygons)
        console.log('loadAndRenderLocationPolygons: Completed successfully')
      } catch (error) {
        console.error('Error loading location polygons:', error)
        console.error('Error details:', error.response || error.message)
        if (this.$toast) {
          this.$toast.error('حدث خطأ أثناء تحميل المشاريع القريبة.')
        }
      }
    },

    drawLocationPolygons(polygons) {
      if (!this.locationPolygonsLayer || !this.arcgisModules) {
        console.warn('drawLocationPolygons: Layer or modules not ready', {
          layer: !!this.locationPolygonsLayer,
          modules: !!this.arcgisModules,
        })
        return
      }

      console.log('drawLocationPolygons: Starting to draw', polygons.length, 'polygons')

      const { Graphic } = this.arcgisModules
      this.locationPolygonsLayer.removeAll()

      let firstGeometry = null
      let drawnCount = 0

      polygons.forEach((location, index) => {
        console.log(`drawLocationPolygons: Processing polygon ${index + 1}:`, location)

        if (!location || !Array.isArray(location.polygon) || location.polygon.length < 3) {
          console.warn(`drawLocationPolygons: Skipping invalid polygon ${index + 1}:`, location)
          return
        }

        // Convert MapPoint[] to ArcGIS rings [[lng, lat], ...]
        const rings = [
          location.polygon.map(coord => [
            Number(coord.longitude),
            Number(coord.latitude),
          ]),
        ]

        const polygon = {
          type: 'polygon',
          rings,
          spatialReference: { wkid: 4326 },
        }

        const fillColor = this.hexToRgbaArray(location.color || '#FF0000', 0.25)

        const symbol = {
          type: 'simple-fill',
          color: fillColor,
          outline: {
            color: [fillColor[0], fillColor[1], fillColor[2], 1],
            width: 2,
          },
        }

        const owners = Array.isArray(location.owners) ? location.owners.join(', ') : ''

        const graphic = new Graphic({
          geometry: polygon,
          symbol,
          attributes: {
            location_id: location.location_id,
            project_id: location.project_id,
            project_name: location.project_name,
            center_id: location.center_id,
            owners,
            distance_meters: location.distance_meters,
          },
          popupTemplate: {
            title: location.project_name || 'موقع',
            content: () => {
              const distance = location.distance_meters != null
                ? `${Number(location.distance_meters).toFixed(2)} م`
                : null

              let html = '<div>'
              html += `<div><strong>المشروع:</strong> ${location.project_name || '-'}</div>`
              if (owners) {
                html += `<div><strong>المالكون:</strong> ${owners}</div>`
              }
              if (distance) {
                html += `<div><strong>المسافة:</strong> ${distance}</div>`
              }
              html += '</div>'
              return html
            },
          },
        })

        if (!firstGeometry) firstGeometry = graphic.geometry

        this.locationPolygonsLayer.add(graphic)
        drawnCount++
        console.log(`drawLocationPolygons: Added polygon ${index + 1} to layer`)
      })

      console.log(`drawLocationPolygons: Successfully drew ${drawnCount} polygons`)

      // Center/zoom map to fit all polygons
      if (this.view && this.locationPolygonsLayer && drawnCount > 0) {
        console.log('drawLocationPolygons: Zooming to fit all polygons')

        // Get all graphics from the layer
        const graphics = this.locationPolygonsLayer.graphics.items || []

        if (graphics.length > 0) {
          // Calculate extent from all polygon geometries
          let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let
            maxY = -Infinity

          graphics.forEach(graphic => {
            if (graphic.geometry && graphic.geometry.rings) {
              graphic.geometry.rings.forEach(ring => {
                ring.forEach(coord => {
                  const [lng, lat] = coord
                  if (lng < minX) minX = lng
                  if (lng > maxX) maxX = lng
                  if (lat < minY) minY = lat
                  if (lat > maxY) maxY = lat
                })
              })
            }
          })

          // Create extent and zoom to it with padding
          if (minX !== Infinity && minY !== Infinity && maxX !== -Infinity && maxY !== -Infinity) {
            const { Extent } = this.arcgisModules
            const extent = new Extent({
              xmin: minX,
              ymin: minY,
              xmax: maxX,
              ymax: maxY,
              spatialReference: { wkid: 4326 },
            })

            // Zoom to extent with padding (50 pixels on each side)
            this.view.goTo(extent, {
              padding: 50,
              duration: 1000, // Smooth animation
            }).then(() => {
              console.log('drawLocationPolygons: Successfully zoomed to polygons')
            }).catch(err => {
              console.warn('drawLocationPolygons: Error zooming to polygons:', err)
              // Fallback: just center on first polygon
              if (firstGeometry) {
                this.view.goTo(firstGeometry, { zoom: 15 })
              }
            })
          } else {
            // Fallback: center on first polygon with a reasonable zoom
            if (firstGeometry) {
              this.view.goTo(firstGeometry, { zoom: 15 })
            }
          }
        } else {
          console.warn('drawLocationPolygons: No graphics found in layer')
        }
      } else {
        console.warn('drawLocationPolygons: Cannot zoom - view, layer, or geometries missing')
      }
    },

    async setCurrentToGeolocation() {
      if (!navigator.geolocation) {
        if (this.$toast) {
          this.$toast.error('المتصفح لا يدعم تحديد الموقع الجغرافي (Geolocation).')
        } else {
          alert('المتصفح لا يدعم تحديد الموقع الجغرافي (Geolocation).')
        }
        // Fallback to Cairo default
        this.currentCoordinates.lat = 31.2001
        this.currentCoordinates.lng = 29.9187
        return
      }

      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            err => reject(err),
            {
              enableHighAccuracy: true,
              timeout: 10000, // give more time before timing out
            },
          )
        })
        this.currentCoordinates.lat = pos.coords.latitude
        this.currentCoordinates.lng = pos.coords.longitude
      } catch (e) {
        console.error('Geolocation error:', e)

        // Only show "please allow" message if the user actually denied permission
        // error.code === 1 => PERMISSION_DENIED
        if (e && e.code === 1) {
          if (this.$toast) {
            this.$toast.error('الرجاء السماح للمتصفح بالوصول إلى موقعك لاستخدام الخريطة بدقة.')
          } else {
            alert('تم رفض الوصول إلى الموقع. الرجاء السماح للمتصفح بالوصول إلى موقعك لاستخدام الخريطة بدقة.')
          }
        } else {
          // Other errors (timeout, unavailable, etc.) => generic message
          if (this.$toast) {
            this.$toast.error('تعذّر الحصول على موقعك حالياً، سيتم استخدام الموقع الافتراضي.')
          } else {
            alert('تعذّر الحصول على موقعك حالياً، سيتم استخدام الموقع الافتراضي.')
          }
        }

        // Fallback to Cairo default
        this.currentCoordinates.lat = 31.2001
        this.currentCoordinates.lng = 29.9187
      }
    },
    async fetchAndRenderFixedPoints() {
      try {
        await this.$store.dispatch('fixedPoints/listFixedPoints', {
          query: {
            per_page: 1000, // Get more fixed points for the map
          },
        })
        this.$nextTick(() => {
          this.renderFixedPoints()
        })
      } catch (error) {
        console.error('Error fetching fixed points:', error)
      }
    },
    renderFixedPoints() {
      if (!this.fixedPointsLayer || !this.arcgisModules) {
        console.log('Fixed points layer or modules not ready')
        return
      }
      const { Point, Graphic, PictureMarkerSymbol } = this.arcgisModules
      this.fixedPointsLayer.removeAll()

      // fixedPoints is already an array
      const fixedPointsData = Array.isArray(this.fixedPoints)
        ? this.fixedPoints
        : (this.fixedPoints?.data || [])

      if (!fixedPointsData.length) {
        console.log('No fixed points data to render')
        return
      }

      let renderedCount = 0
      fixedPointsData.forEach(fixedPoint => {
        // Parse latitude/longitude as numbers (they come as strings from API)
        const lat = parseFloat(fixedPoint.latitude)
        const lng = parseFloat(fixedPoint.longitude)

        // Use latitude/longitude if available, otherwise skip
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          return
        }

        const point = new Point({
          latitude: lat,
          longitude: lng,
        })

        // Get color from accuracy_degree_color or default
        const colorHex = fixedPoint.accuracy_degree_color || '#0099ff'

        // Create a pin-style marker using SVG
        const pinSvg = this.createPinIcon(colorHex)

        // Use PictureMarkerSymbol for custom icon
        const symbol = new PictureMarkerSymbol({
          url: pinSvg,
          width: 32,
          height: 40, // Taller to show the pin point
          xoffset: 0,
          yoffset: 20, // Offset to position the pin point at the coordinates
        })

        // Build tooltip content
        const tooltipContent = this.buildFixedPointTooltip(fixedPoint)

        const graphic = new Graphic({
          geometry: point,
          symbol,
          attributes: {
            id: fixedPoint.id,
            code: fixedPoint.code,
            latitude: lat,
            longitude: lng,
            level: fixedPoint.level,
            accuracy_degree: fixedPoint.accuracy_degree_label_ar || fixedPoint.accuracy_degree,
            accuracy_degree_color: fixedPoint.accuracy_degree_color,
            responsible_employee: fixedPoint.responsible_employee
              ? fixedPoint.responsible_employee.full_name
              : '-',
            etm_est: fixedPoint.etm_est,
            etm_north: fixedPoint.etm_north,
            etm_zone: fixedPoint.etm_zone,
            utm_est: fixedPoint.utm_est,
            utm_north: fixedPoint.utm_north,
            utm_zone: fixedPoint.utm_zone,
            created_at: fixedPoint.created_at,
          },
          popupTemplate: {
            title: fixedPoint.code || 'نقطة ثابتة',
            content: tooltipContent,
          },
        })

        this.fixedPointsLayer.add(graphic)
        renderedCount++
      })
      console.log(`Rendered ${renderedCount} fixed point markers on the map`)
    },
    createPinIcon(colorHex) {
      // Create an SVG pin/marker icon with the specified color
      // The pin point is at the bottom center (coordinates location)
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
          <!-- Pin shadow -->
          <ellipse cx="16" cy="36" rx="4" ry="2" fill="rgba(0,0,0,0.2)"/>
          <!-- Pin body -->
          <path d="M16 2C10.477 2 6 6.477 6 12c0 6 10 24 10 24s10-18 10-24C26 6.477 21.523 2 16 2z" 
                fill="${colorHex}" 
                stroke="#ffffff" 
                stroke-width="2"/>
          <!-- Pin center circle -->
          <circle cx="16" cy="12" r="5" fill="#ffffff"/>
          <circle cx="16" cy="12" r="3" fill="${colorHex}"/>
        </svg>
      `.trim()
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
    },
    buildFixedPointTooltip(fixedPoint) {
      const fields = [
        { fieldName: 'code', label: 'الكود' },
        { fieldName: 'level', label: 'المستوى' },
        { fieldName: 'accuracy_degree', label: 'درجة الدقة' },
        { fieldName: 'responsible_employee', label: 'الموظف المسؤول' },
      ]

      // Add coordinates if available
      if (fixedPoint.latitude && fixedPoint.longitude) {
        fields.push(
          {
            fieldName: 'latitude',
            label: 'خط العرض',
            format: {
              places: 6,
              digitSeparator: true,
            },
          },
          {
            fieldName: 'longitude',
            label: 'خط الطول',
            format: {
              places: 6,
              digitSeparator: true,
            },
          },
        )
      }

      // Add ETM if available
      if (fixedPoint.etm_est && fixedPoint.etm_north) {
        fields.push(
          { fieldName: 'etm_est', label: 'ETM East' },
          { fieldName: 'etm_north', label: 'ETM North' },
          { fieldName: 'etm_zone', label: 'ETM Zone' },
        )
      }

      // Add UTM if available
      if (fixedPoint.utm_est && fixedPoint.utm_north) {
        fields.push(
          { fieldName: 'utm_est', label: 'UTM East' },
          { fieldName: 'utm_north', label: 'UTM North' },
          { fieldName: 'utm_zone', label: 'UTM Zone' },
        )
      }

      return [
        {
          type: 'fields',
          fieldInfos: fields,
        },
      ]
    },
    hexToRgbaArray(hex, alpha = 1) {
      if (!hex || typeof hex !== 'string' || !/^#([0-9A-F]{6})$/i.test(hex)) {
        return [0, 128, 255, alpha]
      }
      return [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
        alpha,
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.arcgis-map-container {
  .map-controls {
    background: #fff;
    padding: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .map-view {
    height: 500px;
    width: 100%;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .map-info {
    background: #f8f9fa;
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
  }
}

// ArcGIS specific styles
#viewDiv {
  padding: 0;
  margin: 0;
  height: 500px;
  width: 100%;
}

// Responsive adjustments
@media (max-width: 768px) {
  .arcgis-map-container {
    .map-view,
    #viewDiv {
      height: 400px;
    }

    .map-controls {
      .row .col-md-6 {
        margin-bottom: 1rem;
      }
    }
  }
}

.map-container-wrapper {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #06284D;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
